import { useEffect, useState } from 'react'
import { bangkokNowMs } from '@/lib/batchAvailability'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5050/api'

// แคมเปญที่กำลังแสดงอยู่ + ป๊อปอัปหน้าแรก — GET /api/campaigns/active (ไม่ต้องล็อกอิน)
// ตัวเลขส่วนลด/ข้อความเงื่อนไข/วันหมดอายุประกอบจากตัวคูปองที่ API (ไม่ใช่ค่าที่พิมพ์เอง) · ไม่มีแคมเปญ = campaign เป็น null

export interface CampaignCoupon {
  code: string
  title: string          // เช่น "ลด 35%"
  detail: string         // เช่น "คอร์สออนไลน์ · ลดสูงสุด ฿1,500 · ยอดขั้นต่ำ ฿1,000"
  expires_at: string | null
}

export interface ActiveCampaign {
  campaign_id: number
  color: string          // #RRGGBB
  ends_at: string        // เวลาไทยไม่มี timezone
  show_countdown: boolean
  banner: { top: string | null; main: string | null } | null
  floating: { headline: string | null; sub: string | null; button: string | null } | null
  coupons: CampaignCoupon[]
}

export interface PopupItem {
  coupon_id: number
  code: string
  badge: string | null
  headline: string | null
  subtitle: string | null
  note: string | null
  color: string
  pattern: 'hex' | 'dots' | 'wave' | string
  discount: string       // ตัวเลขใหญ่ เช่น 35% / ฿300
  expires_at: string | null
  version: string | null // เปลี่ยนเมื่อแอดมินแก้เนื้อหา → ผู้เยี่ยมชมเห็นป๊อปอัปใหม่อีกครั้ง
}

export interface ActivePromo {
  campaign: ActiveCampaign | null
  popups: PopupItem[]
  /** เวลา server − เวลาไทยจากนาฬิกาเครื่อง (มิลลิวินาที) — บวกเข้านาฬิกาเครื่องแล้วได้เวลาที่ตรงกับ server (ใช้นับถอยหลัง) */
  clockOffsetMs: number
}

const EMPTY: ActivePromo = { campaign: null, popups: [], clockOffsetMs: 0 }
const REFRESH_MS = 5 * 60 * 1000   // เช็กใหม่ทุก 5 นาที (แคมเปญสลับ/หมดเวลา/คูปองถูกปิด)

export async function fetchActivePromo(): Promise<ActivePromo> {
  try {
    const res = await fetch(`${API_URL}/campaigns/active`)
    const json = await res.json()
    if (!json?.success || !json.data) return EMPTY
    const data = json.data
    // server_now เป็นเวลาไทยแบบไม่มี timezone — ตีความเป็นเวลาเครื่องเหมือน bangkokNowMs() จึงลบกันได้ตรงๆ
    const serverMs = data.server_now ? new Date(data.server_now).getTime() : NaN
    return {
      campaign: data.campaign ?? null,
      popups: Array.isArray(data.popups) ? data.popups : [],
      clockOffsetMs: Number.isFinite(serverMs) ? serverMs - bangkokNowMs() : 0,
    }
  } catch {
    return EMPTY   // โหลดไม่ได้ = ไม่แสดงอะไร (โปรโมชันไม่ใช่ของจำเป็น ห้ามกระทบหน้าอื่น)
  }
}

// ── ตัวเก็บกลางของแอป: ทุกคอมโพเนนต์ (แถบ/ปุ่มลอย/ป๊อปอัป) ใช้ข้อมูลชุดเดียวกัน ยิง API ครั้งเดียวต่อรอบ ──
let current: ActivePromo | null = null
let inflight: Promise<void> | null = null
let timer: ReturnType<typeof setInterval> | null = null
const listeners = new Set<(p: ActivePromo) => void>()

const refresh = () => {
  if (!inflight) {
    inflight = fetchActivePromo().then((p) => {
      current = p
      listeners.forEach((fn) => fn(p))
    }).finally(() => { inflight = null })
  }
  return inflight
}

export function useActivePromo(): ActivePromo | null {
  const [promo, setPromo] = useState<ActivePromo | null>(current)
  useEffect(() => {
    listeners.add(setPromo)
    if (current) setPromo(current)
    else refresh()
    if (!timer) timer = setInterval(refresh, REFRESH_MS)
    return () => {
      listeners.delete(setPromo)
      if (listeners.size === 0 && timer) { clearInterval(timer); timer = null }
    }
  }, [])
  return promo
}
